<script setup>
import { RouterView } from "vue-router";
import { onMounted } from "vue";
import { useThemeStore } from "@/stores";
import { useIdleTimeout } from "@/composables/useIdleTimeout";

const themeStore = useThemeStore();
onMounted(() => {
  themeStore.initTheme();
});

useIdleTimeout();
</script>

<!-- src/App.vue -->
<template>
  <router-view v-slot="{ Component }">
    <component :is="Component" />
  </router-view>
</template>

<style lang="scss" scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
    padding-right: calc(var(--section-gap) / 2);
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}
</style>

<!-- Element Plus MessageBox：teleport 到 body，需用全局样式兜底换行和层级 -->
<style lang="scss">
.el-overlay-message-box {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 16px;
}

.el-overlay-message-box .el-message-box {
  box-sizing: border-box;
  width: min(440px, calc(100vw - 32px)) !important;
  min-width: 0 !important;
  max-width: calc(100vw - 32px);
  background: #ffffff !important;
  color: #1f2937 !important;
  opacity: 1 !important;
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 20px 54px rgba(15, 23, 42, 0.22);
}

.el-overlay-message-box .el-message-box__message,
.el-overlay-message-box .el-message-box__message p {
  color: #334155 !important;
  white-space: pre-line;
  line-height: 1.7;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.el-overlay-message-box .el-message-box__title {
  color: #111827 !important;
  font-weight: 700;
}

.el-overlay-message-box .el-message-box__content {
  color: #334155 !important;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.el-overlay-message-box .el-message-box__headerbtn .el-message-box__close {
  color: #64748b !important;
}

.el-overlay-message-box .el-message-box__btns .el-button {
  opacity: 1 !important;
}

.el-overlay.pgy-idle-timeout-msgbox-overlay {
  z-index: 20000 !important;
}

.pgy-idle-timeout-msgbox {
  z-index: 20001 !important;
  width: min(420px, calc(100vw - 32px)) !important;
  min-width: 0 !important;
  background: #ffffff !important;
  color: #1f2937 !important;
  opacity: 1 !important;
}

.pgy-idle-timeout-msgbox .el-message-box__message,
.pgy-idle-timeout-msgbox .el-message-box__message p {
  white-space: normal;
  line-height: 1.7;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.pgy-confirm-message-box {
  position: relative;
  width: min(440px, calc(100vw - 32px)) !important;
  min-width: 0 !important;
  min-height: 180px;
  resize: vertical;
  overflow: auto;
  background: linear-gradient(180deg, #f6fbff 0%, #ffffff 45%) !important;
  color: #334155;
  border: 1px solid rgba(64, 158, 255, 0.16);
  border-radius: 18px;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.16);
  text-align: left;
}

.pgy-confirm-message-box .el-message-box__header {
  position: relative;
  cursor: move;
  padding: 22px 52px 10px 26px;
  background: linear-gradient(
    135deg,
    rgba(64, 158, 255, 0.14),
    rgba(103, 194, 58, 0.08)
  );
  border-bottom: 1px solid rgba(64, 158, 255, 0.12);
}

.pgy-confirm-message-box .el-message-box__headerbtn {
  position: absolute;
  top: 18px;
  right: 18px;
  left: auto;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.72);
  transition:
    background-color 0.2s ease,
    transform 0.2s ease;
}

.pgy-confirm-message-box .el-message-box__headerbtn:hover {
  background: #fff;
  transform: scale(1.04);
}

.pgy-confirm-message-box .el-message-box__title {
  color: #0f4c81;
  font-size: 18px;
  font-weight: 700;
  text-align: left;
  letter-spacing: 0.2px;
}

.pgy-confirm-message-box .el-message-box__content {
  color: #475569;
  font-size: 15px;
  line-height: 1.7;
  padding: 18px 26px 14px;
  background: transparent;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.pgy-confirm-message-box .el-message-box__message,
.pgy-confirm-message-box .el-message-box__message p {
  white-space: pre-line;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.pgy-confirm-message-box .el-message-box__btns {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 10px 26px 22px;
  text-align: right;
  background: transparent;
}

.pgy-confirm-message-box .el-message-box__btns .el-button + .el-button {
  margin-left: 0;
}

.pgy-confirm-message-box .el-message-box__btns .el-button {
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 600;
}
</style>
